/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Alert, Cell, Grid, Heading, Modal, ModalBody, Text, VFlow } from 'bold-ui'
import { PageLoading } from 'components/loading'
import { SearchInput } from 'components/search/SearchInput'
import { useProblemasModalQuery } from 'graphql/hooks.generated'
import { Problema } from 'graphql/types.generated'
import React, { useState } from 'react'
import { useHistory, useRouteMatch } from 'react-router'
import formatProblemas from 'view/atendimentos/detail/soap/aside/lista-problemas/formatProblemas'
import { ProblemaCondicaoModel } from 'view/atendimentos/detail/soap/avaliacao/components/problemas-condicoes/model'

import { AccordionProblemasModal } from './AccordionProblemasModal'

interface ListaProblemasModalProps {
  prontuarioId: ID
  dataNascimentoCidadao: LocalDate
  problemasAvaliados: ProblemaCondicaoModel[]
}

export function ListaProblemasModal(props: ListaProblemasModalProps) {
  const { prontuarioId, dataNascimentoCidadao, problemasAvaliados } = props
  const history = useHistory()
  const match = useRouteMatch()

  const [filtroResolvidos, setFiltroResolvidos] = useState('')

  const { data, loading } = useProblemasModalQuery({
    variables: { filtro: { prontuarioId: prontuarioId } },
  })

  const todosProblemas = formatProblemas(data?.problemas?.content ?? [], problemasAvaliados)
  const problemasAtivosLatentes = todosProblemas.getProblemasAtivosLatentes()
  const problemasResolvidos = todosProblemas
    .getProblemasResolvidos()
    ?.filter(problemaMatchesFilter(filtroResolvidos.toUpperCase()))

  const onClose = () => history.push(match.url.replace('/lista-problemas-condicoes', ''))

  const handleSearchResolvidos = (filter: string) => {
    setFiltroResolvidos(filter)
  }

  return (
    <Modal open={true} onClose={onClose}>
      <ModalBody>
        <Grid>
          <Cell size={12}>
            <Heading
              level={1}
              style={css`
                margin-bottom: 1rem;
              `}
            >
              Lista de problemas/condições
            </Heading>
            <Alert inline type='info'>
              Os problemas clínicos do cidadão podem ser adicionados na Lista de problemas/condições durante o registro
              na Avaliação. Problema clínico é tudo aquilo que requeira diagnóstico e manejo posterior, ou que interfira
              com a qualidade de vida do cidadão.
            </Alert>
          </Cell>
          {loading && <PageLoading message='Carregando dados' />}
          {!loading && (
            <React.Fragment>
              <Cell size={12}>
                <Heading level={2}>Ativos/Latentes</Heading>
                <AccordionProblemasModal
                  problemas={problemasAtivosLatentes}
                  dataNascimentoCidadao={dataNascimentoCidadao}
                />
                {problemasAtivosLatentes.length === 0 && (
                  <Text component='p'>Nenhum problema/condição ativo ou latente registrado.</Text>
                )}
              </Cell>
              <Cell size={12}>
                <VFlow vSpacing={0.5}>
                  <Heading level={2}>Resolvidos</Heading>

                  <div
                    css={css`
                      width: 19rem;
                    `}
                  >
                    <SearchInput placeholder='Pesquise por um problema/condição' onSearch={handleSearchResolvidos} />
                  </div>

                  <AccordionProblemasModal
                    problemas={problemasResolvidos}
                    dataNascimentoCidadao={dataNascimentoCidadao}
                    filtro={filtroResolvidos}
                  />
                  {problemasResolvidos.length === 0 && (
                    <Text component='p'>
                      {filtroResolvidos
                        ? 'Nenhum resultado encontrado.'
                        : 'Nenhum problema/condição resolvido registrado.'}
                    </Text>
                  )}
                </VFlow>
              </Cell>
            </React.Fragment>
          )}
        </Grid>
      </ModalBody>
    </Modal>
  )
}

const problemaMatchesFilter = (filter: string) => (problema: Problema) =>
  (problema.ciap && (problema.ciap.codigo.includes(filter) || problema.ciap.descricao.includes(filter))) ||
  (problema.cid10 && (problema.cid10.codigo.includes(filter) || problema.cid10.nome.includes(filter))) ||
  (problema.descricaoOutro && problema.descricaoOutro.includes(filter))
